// src/core/utils/numberUtils.ts

/** Persian (۰-۹) and Arabic-Indic (٠-٩) digit characters */
const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const ARABIC_INDIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
const ENGLISH_DIGITS = '0123456789';

/**
 * Convert Persian and Arabic-Indic digits to English digits in a string.
 * Also converts Arabic decimal separator (٫) to period (.).
 * Use for number inputs so users can type ۱۲۳ or ١٢٣ and the value is stored as 123.
 */
export function persianToEnglishNumbers(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  let result = '';
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    const persianIdx = PERSIAN_DIGITS.indexOf(ch);
    const arabicIdx = ARABIC_INDIC_DIGITS.indexOf(ch);
    if (persianIdx !== -1) result += ENGLISH_DIGITS[persianIdx];
    else if (arabicIdx !== -1) result += ENGLISH_DIGITS[arabicIdx];
    else if (ch === '٫' || ch === '٬') result += '.'; // Arabic decimal / thousands
    else result += ch;
  }
  return result;
}

/** Format number with 3-digit comma separator (e.g. 1,000,000,000) */
export const formatNumberWithComma = (number: number | string): string => {
  if (number === '' || number === null || number === undefined) return '';
  const str = String(number).replace(/,/g, '');
  if (str === '' || str === '-') return str;
  const num = parseFloat(str);
  if (Number.isNaN(num)) return String(number);
  const parts = num.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.length > 1 ? parts.join('.') : parts[0];
};

/** Parse comma-formatted string to number (for form submit / API) */
export const parseFormattedNumber = (value: string | number): number => {
  if (value === '' || value === null || value === undefined) return 0;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const str = persianToEnglishNumbers(String(value)).replace(/,/g, '').trim();
  if (str === '') return 0;
  const num = parseFloat(str);
  return Number.isNaN(num) ? 0 : num;
};

export const separate = (number: number | string | null | undefined): string => {
  if (number === '' || number === null || number === undefined) return '';
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const splitComma = (value: string): number => {
  if (!value) return 0;
  return +value.split(',').join('');
};

export const formatNumber = (number: number | string): string => {
  return separate(number);
};

export const parseNumber = (value: string | number): number => {
  return parseFormattedNumber(value);
};

export const splitDot = (value: string): string => {
  if (!value) return '';
  return value.split('.')[0];
};
